import { cn } from '@packages/shared/src/utils/cn/cn';
import { BusinessTermsCheckbox } from './BusinessTermsCheckbox.client';
import { BusinessTermsText } from './BusinessTermsText.client';
import { WeeeTakeBackHint } from './WeeeTakeBackHint.client';

export type BusinessTermsProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> & {
  /**
   * When the customer has to reconfirm the terms and conditions
   * (basket.reconfirmTermsAndConditions.required), the legal text is rendered
   * as a checkbox that has to be ticked before the order can be placed.
   */
  isCheckable?: boolean;
  isChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Show the inline error below the checkbox (submit attempted while unchecked) */
  showUncheckedError?: boolean;
  /** basket.showWEEETakeBackHint — hint about the WEEE old-device take-back directive */
  showWEEETakeBackHint?: boolean;
};

/**
 * The legal-terms block on the order pages. Renders the plain legal text
 * (`BusinessTermsText`) by default, or the retroactive AGB consent
 * (`BusinessTermsCheckbox`) when `isCheckable` is set, plus the optional WEEE
 * take-back hint. Owns only the shared framing; each variant is a self-contained
 * component that receives just what it needs.
 */
export const BusinessTerms = ({
  isCheckable = false,
  isChecked = false,
  onCheckedChange,
  showUncheckedError = false,
  showWEEETakeBackHint = false,
  className,
  ...rest
}: BusinessTermsProps) => (
  <div
    className={cn(
      'border-color-grey text-color-text-darktransparent bg-color-grey-light border-t p-1',
      !isCheckable && 'text-center',
      className,
    )}
    {...rest}
  >
    {isCheckable ? (
      <BusinessTermsCheckbox
        isChecked={isChecked}
        onCheckedChange={onCheckedChange}
        showUncheckedError={showUncheckedError}
      />
    ) : (
      <BusinessTermsText />
    )}
    {showWEEETakeBackHint && <WeeeTakeBackHint />}
  </div>
);
